import { css, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { Page } from "../enums/Page";
import { GameLevelRouteService, LevelProgressResponse } from "../services/gameLevelRouteService";

/** CSS for LevelSelectorComponent */
const styles: string = css`
    :host {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: center;
        height: 100vh;
        width: 100%;
        font-family: "Onesize";
        background-image: url('/assets/img/startup_img.png');
        background-size: cover;
        background-position: center;
        color: white;
        text-align: center;
    }

    h2 {
        font-size: 2.5rem;
        margin-bottom: 2rem;
    }

    .level-button {
        border: 2px solid #332c57;
        padding: 8px 20px;
        font-size: 1.5rem;
        border-radius: 8px;
        cursor: pointer;
        text-transform: uppercase;
        transition: 0.2s ease;
    }

    .level-button.completed {
        border: none;
        background-color: #1faa59; /* green */
    }

    .level-button.unlocked {
        background-color: #1900ff; /* blue */
    }

    .level-button.locked {
        background-color: #555;
        opacity: 0.5;
        cursor: not-allowed;
    }

    .level-button.unlocked:hover,
    .level-button.completed:hover {
        transform: scale(1.05);
    }


    .level-button.unlocked:hover {
        background-color: #3355ff;
    }

    .back-button {
        background-image: url("/assets/img/ui/button_gray.png");
        background-size: 100% 100%;
        background-position: center;
        background-repeat: no-repeat;
        margin-top: 20px;
        padding: 10px 20px;
        border-radius: 8px;
        cursor: pointer;
    }

    .back-button:hover {
        background-image: url("/assets/img/ui/button_gray_hover.png");
        background-size: 100% 100%;
        background-position: center;
        background-repeat: no-repeat;
    }

    .credits {
        position: absolute;
        top: 0;
        left: 0;
        font-size: 1rem;
        color: rgba(255, 255, 255, 0.7);
    }
    @media (max-width: 400px) {
        .credits {
            font-size: 0.8rem;
        }
        .level-button {
            font-size: 1.2rem;
        }
    }
`;

export class LevelSelectorComponent extends HTMLElement {
    private readonly _gameEventService: GameEventService = new GameEventService();
    private readonly _levelRouteService: GameLevelRouteService = new GameLevelRouteService();

    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        void this.init();
    }

    private async init(): Promise<void> {
        let levelData: LevelProgressResponse;

        try {
            levelData = await this._levelRouteService.getLevels();
        }
        catch (err) {
            console.error("Could not fetch levels:", err);

            levelData = { completed: [], unlocked: [] };
        }

        this.render(levelData);
    }

    private render(levelData: { completed: string[]; unlocked: string[] }): void {
        if (!this.shadowRoot) {
            return;
        }

        // Master list of all levels
        const levels: { alias: string; label: string }[] = [
            { alias: "introduction-room", label: "Level 01" },
            { alias: "guard-station", label: "Level 02" },
            { alias: "library-room", label: "Level 03" },
            { alias: "candyroom", label: "Level 04" },
            { alias: "the-angry-teacher", label: "Level 05" },
            { alias: "dentistroom", label: "Level 06" },
            { alias: "brewery", label: "Level 07" },
            { alias: "knights-chamber", label: "Level 08" },
            { alias: "monster-attack-room", label: "Level 09" },
            { alias: "final-gate-room", label: "Level 10" },
        ];

        const elements: HTMLElement[] = htmlArray`
            <style>${styles}</style>

            <h2>Select Level</h2>

            ${levels.map(level => {
            const isCompleted: boolean = levelData.completed.includes(level.alias);
            const isUnlocked: boolean = levelData.unlocked.includes(level.alias);
            let stateClass: string = "locked";

            if (isCompleted) {
                stateClass = "completed";
            }
            else if (isUnlocked) {
                stateClass = "unlocked";
            }

            return htmlArray`
                    <div 
                        class="level-button ${stateClass}" 
                        data-level="${isUnlocked ? level.alias : ""}"
                    >
                        ${level.label}
                    </div>
                `;
        }).flat()}

            <div class="back-button" id="backButton">Back</div>
            <div class="credits">© 2026 PB3-SE101 - Team 2</div>
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);

        this.shadowRoot.querySelectorAll(".level-button.unlocked, .level-button.completed")
            .forEach(button => {
                button.addEventListener("click", () => {
                    const levelAlias: string | undefined = (button as HTMLElement).dataset.level;

                    if (!levelAlias) {
                        return;
                    }

                    void (async () => {
                        try {
                            await this._levelRouteService.selectActiveLevel(levelAlias);
                            console.log("Backend activated level:", levelAlias);
                            this._gameEventService.switchPage(Page.Canvas);
                        }
                        catch (err) {
                            console.error("Failed to select level on backend:", err);
                        }
                    })();
                });
            });

        this.shadowRoot.getElementById("backButton")?.addEventListener("click", () => {
            this._gameEventService.switchPage(Page.Startup);
        });
    }
}

window.customElements.define("level-selector", LevelSelectorComponent);
